const EXCLUDED_DO_PROPERTIES = ['relatedtickets', 'template', 'closed', 'agenttime'];

/**
 * The properties a "Do" row may pick: everything settable, minus what the other rows already set. A ticket
 * property can only hold one value, so a second row for it would silently overwrite the first. The row's own
 * property stays in its list so an existing selection still renders and can be kept.
 */
export const getAvailableDoProperties = <T extends { key: string }>(
  propertiesList: T[],
  rows: { Property?: string }[],
  rowIndex?: number
): T[] => {
  const usedByOtherRows = new Set(
    rows
      .filter((_, i) => i !== rowIndex)
      .map(row => row.Property?.toLowerCase())
      .filter(Boolean)
  );

  return propertiesList.filter(
    x => !EXCLUDED_DO_PROPERTIES.includes(x.key) && !usedByOtherRows.has(x.key?.toLowerCase())
  );
};

/** The settable properties that another row has already taken, i.e. what getAvailableDoProperties held back. */
export const getPropertiesUsedByOtherRows = <T extends { key: string }>(
  propertiesList: T[],
  rows: { Property?: string }[],
  rowIndex?: number
): T[] => {
  const available = new Set(getAvailableDoProperties(propertiesList, rows, rowIndex).map(x => x.key));
  return propertiesList.filter(x => !EXCLUDED_DO_PROPERTIES.includes(x.key) && !available.has(x.key));
};

/**
 * The already-added property a search was most likely looking for, so an empty result can say why instead of
 * claiming nothing matched. Mirrors the dropdown's own filter (header contains the query), preferring an exact
 * then a prefix match when several were added.
 */
export const findAlreadyAddedProperty = <T extends { header?: string }>(searchQuery: string, alreadyAdded: T[]): T | undefined => {
  const query = searchQuery?.trim().toLowerCase();
  if (!query || !alreadyAdded?.length) return undefined;

  const header = (x: T) => x.header?.toLowerCase() ?? '';
  return alreadyAdded.find(x => header(x) === query)
    ?? alreadyAdded.find(x => header(x).startsWith(query))
    ?? alreadyAdded.find(x => header(x).includes(query));
};

/**
 * The property names that appear more than once, in the casing of their first appearance. Blank names are rows
 * the user has not filled in yet, not duplicates. SaveAutomationFlow rejects the same set server-side.
 */
export const findPropertiesSetMoreThanOnce = (propertyNames: string[]): string[] => {
  const firstSeen = new Map<string, string>();
  const repeated = new Set<string>();
  for (const name of propertyNames) {
    if (!name?.trim()) continue;
    const normalized = name.toLowerCase();
    if (firstSeen.has(normalized)) repeated.add(firstSeen.get(normalized));
    else firstSeen.set(normalized, name);
  }
  return [...repeated];
};
